import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import {
  HiOutlineFilter, HiOutlineCash, HiOutlineScale,
  HiOutlineCalendar, HiOutlineChevronDown, HiOutlineChevronUp,
} from 'react-icons/hi';

const fmtBDT = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;

function rangeForPreset(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  const getSaturday = (d) => {
    const dow = d.getDay();
    const diff = (dow + 1) % 7;
    const sat = new Date(d);
    sat.setDate(d.getDate() - diff);
    return sat;
  };

  if (preset === 'today') return [fmt(today), fmt(today)];
  if (preset === 'thisWeek') {
    const start = getSaturday(today);
    return [fmt(start), fmt(today)];
  }
  if (preset === 'lastWeek') {
    const thisSat = getSaturday(today);
    const lastSat = new Date(thisSat);
    lastSat.setDate(thisSat.getDate() - 7);
    const lastFri = new Date(lastSat);
    lastFri.setDate(lastSat.getDate() + 6);
    return [fmt(lastSat), fmt(lastFri)];
  }
  if (preset === 'thisMonth' || preset === 'lastMonth') {
    const d = new Date(today);
    if (preset === 'lastMonth') d.setMonth(d.getMonth() - 1);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return [fmt(start), fmt(end)];
  }
  if (preset === 'last7') {
    const start = new Date(today); start.setDate(start.getDate() - 6);
    return [fmt(start), fmt(today)];
  }
  if (preset === 'last30') {
    const start = new Date(today); start.setDate(start.getDate() - 29);
    return [fmt(start), fmt(today)];
  }
  return [fmt(today), fmt(today)];
}

const RANGE_PRESETS = [
  { value: 'today',     label: 'আজ' },
  { value: 'thisWeek',  label: 'এই সপ্তাহ' },
  { value: 'lastWeek',  label: 'গত সপ্তাহ' },
  { value: 'thisMonth', label: 'এই মাস' },
  { value: 'lastMonth',   label: 'গত মাস' },
  { value: 'last3Months', label: 'গত ৩ মাস' },
  { value: 'lastWeek',    label: 'গত সপ্তাহ' },
];

const WorkerSalary = () => {
  const [salaries, setSalaries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangePreset, setRangePreset] = useState('thisWeek');
  const [currentWeekSalary, setCurrentWeekSalary] = useState(null);
  const { t } = useLanguage();

  const [rangeStart, rangeEnd] = useMemo(() => {
    return rangeForPreset(rangePreset);
  }, [rangePreset]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { start_date: rangeStart, end_date: rangeEnd };
      const [salariesRes, cwRes] = await Promise.all([
        API.get('/worker/salary/my', { params }),
        API.get('/worker/salary/current-week'),
      ]);
      let allSalaries = salariesRes.data.salaries || [];
      const cw = cwRes.data.salary;

      // Merge current week into history if not already present
      if (cw && !allSalaries.find((s) => s.id === cw.id)) {
        allSalaries = [cw, ...allSalaries];
      }

      setSalaries(allSalaries);
      setStats(salariesRes.data.stats);
      setCurrentWeekSalary(cw);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Aggregates for the date display
  const bengaliMonth = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('bn-BD', { month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader title={t('salary')} subtitle={`${rangeStart} → ${rangeEnd} · ${salaries.length} টি রেকর্ড`} />

      {/* ── This Week Card (always highlighted) ──────────────────────────── */}
      {currentWeekSalary && (
        <div className="card border-2 border-brand-400 dark:border-brand-600 bg-gradient-to-br from-brand-50 via-white to-accent-50 dark:from-brand-900/20 dark:via-ink-800 dark:to-accent-900/20">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[11px] font-bold uppercase tracking-wider">
                  <HiOutlineCalendar className="w-3 h-3" />
                  চলতি সপ্তাহ
                </span>
                <Badge variant={currentWeekSalary.status === 'paid' ? 'success' : 'warning'} dot>
                  {currentWeekSalary.status === 'paid' ? 'পরিশোধিত' : 'বকেয়া'}
                </Badge>
              </div>
              <p className="text-xs text-ink-500 dark:text-ink-400">
                {currentWeekSalary.week_start} → {currentWeekSalary.week_end}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-ink-500 dark:text-ink-400">নিট বেতন</p>
              <p className="text-3xl font-bold text-brand-700 dark:text-brand-300">{fmtBDT(currentWeekSalary.net_amount)}</p>
            </div>
          </div>

          {/* Weekly breakdown */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-brand-200 dark:border-brand-800">
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">
                <span>📅</span> কাজের দিন
              </p>
              <p className="text-lg font-bold text-ink-900 dark:text-white">{Number(currentWeekSalary.total_days).toFixed(1)}</p>
            </div>
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">
                <span>💰</span> মোট আয়
              </p>
              <p className="text-lg font-bold text-ink-900 dark:text-white">{fmtBDT(currentWeekSalary.gross_amount)}</p>
            </div>
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">
                <span>💸</span> অগ্রিম কর্তন
              </p>
              <p className={`text-lg font-bold ${Number(currentWeekSalary.total_advance) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-ink-500'}`}>
                {Number(currentWeekSalary.total_advance) > 0 ? `−${fmtBDT(currentWeekSalary.total_advance)}` : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">
                <span>✅</span> হাতে পাবেন
              </p>
              <p className="text-lg font-bold text-brand-700 dark:text-brand-300">{fmtBDT(currentWeekSalary.net_amount)}</p>
            </div>
          </div>

          {/* Advance breakdown if any */}
          {Number(currentWeekSalary.total_advance) > 0 && (
            <div className="mt-3 p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-xl text-xs">
              <p className="text-rose-700 dark:text-rose-200 font-semibold flex items-center gap-1.5">
                <span>📋</span> অগ্রিম বিস্তারিত
              </p>
              <p className="mt-1 text-rose-600 dark:text-rose-300">
                আপনার মোট <strong>{fmtBDT(currentWeekSalary.gross_amount)}</strong> আয় থেকে
                <strong className="text-rose-700 dark:text-rose-200"> {fmtBDT(currentWeekSalary.total_advance)} </strong>
                আগাম নেওয়ার কারণে কেটে রাখা হয়েছে। হাতে পাবেন <strong>{fmtBDT(currentWeekSalary.net_amount)}</strong>।
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      {!currentWeekSalary && (
        <div className="card text-center py-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <p className="text-lg mb-2">📭</p>
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">এই সপ্তাহের জন্য এখনো বেতন হিসাব করা হয়নি।</p>
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">অ্যাডমিন বেতন হিসাব করলে এখানে দেখা যাবে।</p>
        </div>
      )}

      {/* Filter bar */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <HiOutlineFilter className="w-5 h-5 text-ink-500 dark:text-ink-400" />
          <h2 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider">ফিল্টার</h2>
        </div>
        <div className="flex gap-1.5 p-1 bg-ink-100 dark:bg-ink-800 rounded-xl overflow-x-auto">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setRangePreset(p.value)}
              className={
                'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ' +
                (rangePreset === p.value
                  ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                  : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-2 text-xs text-ink-500 dark:text-ink-400 flex items-center gap-2">
          <HiOutlineCalendar className="w-4 h-4" />
          <span>
            সক্রিয় রেঞ্জ: <strong className="text-ink-900 dark:text-white">{rangeStart}</strong> → <strong className="text-ink-900 dark:text-white">{rangeEnd}</strong>
          </span>
          {stats && (
            <span className="px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[10px] font-semibold">
              {stats.count} টি
            </span>
          )}
        </div>
      </div>

      {/* ── Aggregate cards ─────────────────────────────────────────────── */}
      {stats && (stats.total_net > 0 || salaries.length > 0) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">মোট আয় (Gross)</p>
                <p className="text-2xl font-bold text-ink-900 dark:text-white mt-1">{fmtBDT(stats.total_gross)}</p>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center text-2xl">💰</div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">অগ্রিম কর্তন</p>
                <p className={`text-2xl font-bold mt-1 ${stats.total_advance > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-ink-500'}`}>
                  {stats.total_advance > 0 ? `−${fmtBDT(stats.total_advance)}` : '০'}
                </p>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-2xl">💸</div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">পরিশোধিত</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">{fmtBDT(stats.total_paid)}</p>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-2xl">✅</div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">বকেয়া</p>
                <p className="text-2xl font-bold text-accent-700 dark:text-accent-300 mt-1">{fmtBDT(stats.total_pending)}</p>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-accent-100 dark:bg-accent-900/40 flex items-center justify-center text-2xl">⏳</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Per-week history ──────────────────────────────────────────── */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-brand-500" />
            সাপ্তাহিক বেতনের ইতিহাস
            <span className="text-xs text-ink-400 ml-auto">{salaries.length} টি</span>
          </h2>
        </div>

        {salaries.length === 0 ? (
          <EmptyState icon="💵" title="এই রেঞ্জে কোনো বেতন নেই" description="উপরে ফিল্টার বদলে দেখুন।" />
        ) : (
          <div className="divide-y divide-ink-100 dark:divide-ink-700">
            {salaries.map((s) => {
              const hasAdvance = Number(s.total_advance) > 0;
              return (
                <div key={s.id} className="p-5 hover:bg-ink-50 dark:hover:bg-ink-900/30 transition-colors">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center text-brand-700 dark:text-brand-300 font-bold text-sm">
                        <HiOutlineScale className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-ink-900 dark:text-white">
                          {s.week_start} → {s.week_end}
                        </p>
                        <p className="text-[10px] text-ink-500 dark:text-ink-400">
                          {Number(s.total_days).toFixed(1)} দিন @ {fmtBDT(s.daily_rate)}/দিন
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={s.status === 'paid' ? 'success' : 'warning'} dot={s.status === 'pending'}>
                        {s.status === 'paid' ? t('paid') : s.status === 'partial' ? 'আংশিক' : t('pending')}
                      </Badge>
                      {s.paid_at && (
                        <p className="text-[10px] text-ink-400 mt-0.5">
                          {new Date(s.paid_at).toLocaleDateString('bn-BD', { day: 'numeric', month: 'short' })}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Detail grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="bg-ink-50 dark:bg-ink-900/30 p-3 rounded-xl">
                      <p className="text-[10px] text-ink-500 dark:text-ink-400 uppercase tracking-wider">মোট</p>
                      <p className="font-bold text-ink-900 dark:text-white mt-0.5">{fmtBDT(s.gross_amount)}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${hasAdvance ? 'bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800' : 'bg-ink-50 dark:bg-ink-900/30'}`}>
                      <p className="text-[10px] text-ink-500 dark:text-ink-400 uppercase tracking-wider">অগ্রিম</p>
                      <p className={`font-bold mt-0.5 ${hasAdvance ? 'text-rose-700 dark:text-rose-300' : 'text-ink-500'}`}>
                        {hasAdvance ? `−${fmtBDT(s.total_advance)}` : '—'}
                      </p>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-900/30 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800 sm:col-span-2">
                      <p className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">হাতে পাবেন / পেয়েছেন</p>
                      <div className="flex items-baseline gap-1.5 mt-0.5">
                        <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{fmtBDT(s.net_amount)}</p>
                        {hasAdvance && (
                          <p className="text-[10px] text-rose-600 dark:text-rose-400">
                            ({fmtBDT(Number(s.gross_amount) - Number(s.net_amount))} কেটে নেওয়া হয়েছে)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Advance breakdown note (if advance was cut) */}
                  {hasAdvance && (
                    <details className="mt-2 group">
                      <summary className="text-xs text-ink-500 dark:text-ink-400 cursor-pointer hover:text-ink-700 dark:hover:text-ink-300 flex items-center gap-1">
                        <HiOutlineChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                        অগ্রিম কর্তনের বিস্তারিত
                      </summary>
                      <div className="mt-2 p-2 bg-ink-50 dark:bg-ink-900/30 rounded-lg text-xs text-ink-600 dark:text-ink-400">
                        এই সপ্তাহে আপনার মোট আয় <strong>{fmtBDT(s.gross_amount)}</strong> থেকে
                        <strong className="text-rose-600"> {fmtBDT(s.total_advance)} </strong>
                        অগ্রিম বাবদ কেটে নেওয়া হয়েছে। তাই হাতে পাবেন <strong className="text-emerald-600">{fmtBDT(s.net_amount)}</strong>।
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* No worker salary route info */}
      <div className="card bg-ink-50 dark:bg-ink-900/30 text-xs text-ink-500 dark:text-ink-400 text-center">
        অগ্রিম সংক্রান্ত কোনো প্রশ্ন থাকলে আপনার অ্যাডমিনের সাথে যোগাযোগ করুন।
      </div>
    </div>
  );
};

export default WorkerSalary;